/**
 * The single citation -> in-app path mapping. `ShellProvider.openCitation`
 * uses it to pick a destination tab; SearchPage's own handler reuses it.
 *
 * Returns null for a citation with no in-app destination of its own
 * (competitive-intel external pages, unroutable citations); the shell hands
 * those to the active tab's handler.
 */
import type { SimpleCitation } from '../types/qa';
import { euHistoricalCompareParams } from '../utils/euHistoricalCompareLink';
import { labelVersionDiffParams } from '../utils/labelVersionDiffLink';
import { regulatoryCitationHref } from '../utils/regulatoryCitationHref';

export function citationTargetPath(c: SimpleCitation): string | null {
  if (c.document_type === 'fda_letter' && c.letter_id != null) {
    return c.drug_id != null
      ? `/drug/${c.drug_id}?source=fda-letter&version=${c.letter_id}`
      : `/fda-letter/${c.letter_id}`;
  }
  if (c.document_type === 'label' && c.drug_id != null) {
    return `/drug/${c.drug_id}?section=${encodeURIComponent(c.section_number ?? '')}`;
  }
  if (c.document_type === 'eu_historical' && c.drug_id != null) {
    const params = euHistoricalCompareParams(c);
    return params ? `/drug/${c.drug_id}?${params.toString()}` : null;
  }
  if (c.document_type === 'label_version_diff' && c.drug_id != null) {
    const params = labelVersionDiffParams(c);
    return params ? `/drug/${c.drug_id}?${params.toString()}` : null;
  }
  // Complete archived EU section: the individual-version viewer DrugDetailPage
  // overlays on euhview=1 (regulatoryCitationHref deliberately has no route for it).
  if (c.document_type === 'eu_history_section' && c.drug_id != null
      && (c.source_version_key || c.document_id != null)) {
    const p = new URLSearchParams();
    p.set('euhview', '1');
    // Stable public handle (survives re-ingest); legacy document_id is the
    // back-compat fallback for citations minted before the shim.
    if (c.source_version_key) p.set('euver', c.source_version_key);
    else p.set('euhdoc', String(c.document_id));
    if (c.page != null) p.set('euhpage', String(c.page));
    if (c.highlight_y != null) p.set('euhy', String(c.highlight_y));
    if (c.presentation_key) p.set('euhpres', c.presentation_key);
    if (c.section_number) p.set('euhsection', c.section_number);
    return `/drug/${c.drug_id}?${p.toString()}`;
  }
  if (c.document_type === 'competitive_intel') return null;
  return regulatoryCitationHref(c);
}

export function pathnameOf(path: string): string {
  return path.split(/[?#]/)[0] ?? path;
}

/**
 * An unlinked FDA-letter citation (backend letter reads carry `letter_id` but
 * no `drug_id`) maps to `/fda-letter/:id`; when the tab is a drug page already
 * showing that letter it belongs there, where the section highlight lives.
 */
export function citationOnTabPage(c: SimpleCitation, tabPath: string): boolean {
  if (c.document_type !== 'fda_letter' || c.letter_id == null || c.drug_id != null) return false;
  return tabShowsFdaLetter(tabPath, c.letter_id);
}

/** A drug-page tab path that is showing this FDA letter. */
export function tabShowsFdaLetter(tabPath: string, letterId: number): boolean {
  const u = new URL(tabPath, 'http://shell.local');
  return u.pathname.startsWith('/drug/')
    && u.searchParams.get('source') === 'fda-letter'
    && u.searchParams.get('version') === String(letterId);
}
